import {Injectable} from '@angular/core';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import {Observable} from 'rxjs';
import {IAppState} from '../models/IAppState';
import {IUserData, MemberType} from '../models/IUserData';
import {Store} from '@ngrx/store';
import {ILocalUser} from '../models/ILocalUser';
import {extend} from 'lodash';
import {Platform} from '@ionic/angular';
import {IAuthData} from '../models/IAuthData';
import {environment} from '../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private state$: Observable<IAppState> = this.store.select((state) => state.AppStateReducer);
  private accessToken: string;
  private config: any;
  public localUser: ILocalUser;
  public userData: IUserData;
  public isAndroid = this.platform.is('android');
  public isIos = this.platform.is('ios');
  public authData$: Observable<IAuthData> = this.store.select(
    (state) => state.AppStateReducer.authData
  );

  constructor(
    private platform: Platform,
    private store: Store<{AppStateReducer: IAppState}>
  ) {
    this.authData$.subscribe((authData: IAuthData) => {
      if (authData) {
        this.accessToken = authData.accessToken;
      }
    });
    this.platform.ready().then(() => {
      if (this.isAndroid || this.isIos) {
        this.state$.subscribe(
          (appState: IAppState) => {
            if (appState.userData && !this.userData) {
              this.userData = appState.userData;
              this.localUser = appState.localUser;
              if (this.userData.memberType === MemberType.visitor) {
                this.initConfig();
              }
            }
          });
      }
    });
  }

  initConfig() {
    const reservationId = this.getReservationId();
    this.config = {
      httpRootProperty: '.',
      maxRecordsToPersist: 5,
      reset: true,
      debug: environment.name !== 'production',
      logLevel: BackgroundGeolocation?.LOG_LEVEL_VERBOSE,
      desiredAccuracy: BackgroundGeolocation?.DESIRED_ACCURACY_NAVIGATION,
      // distanceFilter: 2000,
      authorization: {
        strategy: 'JWT',
        accessToken: this.accessToken
      },
      locationTemplate: this.getEtaRequest(reservationId),
      autoSync: true,
      stopOnTerminate: false,
      startOnBoot: true,
      url: `${environment.apiUrl}/reservations/${reservationId}/eta/position`,
      // android only
      fastestLocationUpdateInterval: 2 * 60 * 1000,  // 2m
      locationUpdateInterval: 2 * 60 * 1000 // 2m
    };
  }

  public updateConfig(config) {
    extend(this.config, config);
    BackgroundGeolocation.ready(this.config);
  }

  public configureBackgroundGeolocation(config) {
    extend(this.config, config);

    console.log('BackgroundGeolocation config', this.config);

    if (BackgroundGeolocation) {
      BackgroundGeolocation.ready(this.config, (state) => {
        console.log('[ready] BackgroundGeolocation is ready to use');
        if (!state.enabled) {
          if (this.isTrackingStarted()) {
            this.startTracking();
          }
        }
      });

      BackgroundGeolocation.onHttp((event) => {
        console.log('BackgroundGeolocation onHttp', event);
      });

      BackgroundGeolocation.onLocation((location) => {
        console.log('BackgroundGeolocation onLocation', location);
      });
    }
  }

  public async startTracking(): Promise<any> {
    if (this.isAndroid || this.isIos) {
      await BackgroundGeolocation.ready(this.config, async (state) => {
        console.log('[ready] BackgroundGeolocation started', state);
        await BackgroundGeolocation.stop();
        return BackgroundGeolocation.start();
      });
    }
  }

  public async stopTracking(): Promise<any> {
    if (this.isAndroid || this.isIos) {
      return BackgroundGeolocation.stop();
    }
  }

  public getConfig() {
    return this.config;
  }

  public setInterval(interval: number) {
    console.log('BackgroundGeolocation setInterval', interval);
    this.config.locationUpdateInterval = interval * 1000;
    this.config.fastestLocationUpdateInterval = interval * 1000;
    BackgroundGeolocation.setConfig({
      locationUpdateInterval: this.config.locationUpdateInterval,
      fastestLocationUpdateInterval: this.config.fastestLocationUpdateInterval
    });
  }

  public getEtaRequest(reservationId: string) {
    return JSON.stringify({
      reservationId: reservationId,
      coordinates: {
        locationX: '<%=longitude%>',
        locationY: '<%=latitude%>',
        positionDateTime: '<%=timestamp%>',
        speed: '<%=speed%>',
        accuracy: '<%=accuracy%>',
        heading: '<%=heading%>'
      }
    });
  }

  public getReservationId(): string {
    return localStorage.getItem('reservationId');
  }

  public setReservationId(value: string): void {
    localStorage.setItem('reservationId', value);
  }

  public getEtaEnabled(): boolean {
    return localStorage.getItem('etaEnabled') === 'true';
  }

  public setEtaEnabled(value: boolean): void {
    localStorage.setItem('etaEnabled', value.toString());
  }

  public isTrackingStarted(): boolean {
    return localStorage.getItem('startTracking') === 'true';
  }

  public setTrackingStatus(value: boolean): void {
    localStorage.setItem('startTracking', value.toString());
  }
}
